import { useMemo, useState } from "react";
import { CheckCircle2, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

const FlashCardActivity = ({ activity, completed, onComplete, busy }) => {
  const cards = useMemo(() => activity?.data?.cards || [], [activity]);
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [marks, setMarks] = useState({}); // index -> "got" | "still"

  if (!cards.length) return null;
  const card = cards[i];
  const seenAll = Object.keys(marks).length >= cards.length;

  const mark = (kind) => {
    setMarks({ ...marks, [i]: kind });
    if (i < cards.length - 1) {
      setTimeout(() => { setI(i + 1); setFlipped(false); }, 200);
    }
  };

  return (
    <div data-testid={`activity-flashcards-${activity.title}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-widest font-bold text-mm-steel">
        <span>Card {i + 1} of {cards.length}</span>
        <span>{Object.keys(marks).length} marked</span>
      </div>

      <button
        type="button"
        onClick={() => setFlipped(!flipped)}
        className="mt-3 w-full min-h-[180px] rounded-2xl bg-mm-navy text-white p-8 text-center transition-transform hover:scale-[1.01] focus:outline-none focus:ring-4 focus:ring-mm-gold/40"
        data-testid="flashcard-flip"
        aria-label={flipped ? "Show front" : "Show back"}
      >
        <div className="text-[10px] uppercase tracking-widest text-mm-gold font-bold">{flipped ? "Back" : "Front"}</div>
        <div className="mt-3 font-heading text-2xl font-black leading-snug">
          {flipped ? card.back : card.front}
        </div>
        <div className="mt-4 text-xs text-mm-parchment/60 inline-flex items-center gap-1">
          <RotateCcw size={12} /> Tap card to flip
        </div>
      </button>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button onClick={() => mark("still")} className="btn-trail btn-outline-navy text-sm" data-testid="flashcard-still">Still learning</button>
        <button onClick={() => mark("got")} className="btn-trail btn-gold text-sm" data-testid="flashcard-got">Got it</button>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={() => { if (i > 0) { setI(i - 1); setFlipped(false); } }}
          disabled={i === 0}
          className="text-mm-navy/60 hover:text-mm-navy disabled:opacity-40 inline-flex items-center gap-1 text-sm font-bold"
          data-testid="flashcard-prev"
        ><ChevronLeft size={16} /> Prev</button>
        <div className="flex gap-1">
          {cards.map((c, ci) => (
            <span key={c.front || `card-${ci}`} className={`w-2 h-2 rounded-full ${marks[ci] === "got" ? "bg-mm-gold" : marks[ci] === "still" ? "bg-mm-steel" : "bg-mm-navy/15"}`} />
          ))}
        </div>
        <button
          onClick={() => { if (i < cards.length - 1) { setI(i + 1); setFlipped(false); } }}
          disabled={i >= cards.length - 1}
          className="text-mm-navy/60 hover:text-mm-navy disabled:opacity-40 inline-flex items-center gap-1 text-sm font-bold"
          data-testid="flashcard-next"
        >Next <ChevronRight size={16} /></button>
      </div>

      {!completed && (
        <button
          onClick={() => onComplete()}
          disabled={!seenAll || busy}
          className="btn-trail btn-gold w-full mt-5 disabled:opacity-50"
          data-testid="flashcard-complete"
        >
          {busy ? "Planting marker..." : seenAll ? "Complete activity (+10 AP)" : `Mark all ${cards.length} cards first`}
        </button>
      )}
      {completed && (
        <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-mm-steel/15 text-mm-navy rounded-full font-bold">
          <CheckCircle2 size={16} strokeWidth={2.5} /> Trail marker planted · +10 AP
        </div>
      )}
    </div>
  );
};

export default FlashCardActivity;
